/**
 * NotificationPanel — slide-in panel listing the user's notifications.
 *
 * Matches the design with:
 *   - Header: title + "N new" badge + "Mark all read" + "Clear all"
 *   - Category filter tabs (All, Appointments, System, Patients, Billing, Inventory)
 *   - Date-grouped sections (TODAY, YESTERDAY, EARLIER)
 *   - Each item: icon + title with unread dot + body + time + category tag
 *   - Footer: "Notification settings" + "View all ›"
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import {
  CalendarIcon,
  CapsuleIcon,
  UserPlusIcon,
  CircleDollarSignIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrashIcon,
  CheckIcon,
  PackageIcon,
} from "../icons";
import { Notification } from "../../services/notification.service";
import { SupportUnreadSummary } from "../../services/supportTicket.service";

/* ── Category mapping ─────────────────────────────────────────────────── */

type CategoryKey = "all" | "appointments" | "system" | "patients" | "billing" | "inventory";

const EVENT_TO_CATEGORY: Record<string, CategoryKey> = {
  appointment_confirmed: "appointments",
  appointment_cancelled: "appointments",
  appointment_no_show: "appointments",
  new_appointment_booked: "appointments",
  prescription_ready: "patients",
  new_patient_registered: "patients",
  payment_received: "billing",
  subscription_activated: "system",
  sync_completed: "system",
  sync_failed: "system",
  ai_scribe_ready: "system",
};

function getCategory(eventType: string): CategoryKey {
  return EVENT_TO_CATEGORY[eventType] || "system";
}

const CATEGORY_LABELS: { key: CategoryKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "appointments", label: "Appointments" },
  { key: "system", label: "System" },
  { key: "patients", label: "Patients" },
  { key: "billing", label: "Billing" },
];

/* ── Event type → display category label ──────────────────────────────── */

const EVENT_CATEGORY_LABEL: Record<string, string> = {
  appointment_confirmed: "Appointment",
  appointment_cancelled: "Appointment",
  appointment_no_show: "Appointment",
  new_appointment_booked: "Appointment",
  prescription_ready: "Patient",
  new_patient_registered: "Patient",
  payment_received: "Billing",
  subscription_activated: "System alert",
  sync_completed: "System alert",
  sync_failed: "System alert",
  ai_scribe_ready: "AI",
  inventory_low: "Inventory",
  inventory_restock: "Inventory",
};

/* ── Event type → icon + colors ───────────────────────────────────────── */

const EVENT_VISUAL: Record<string, { icon: React.ReactNode; bg: string; fg: string }> = {
  appointment_confirmed: { icon: <CalendarIcon />, bg: "#dbeafe", fg: "#2563eb" },
  appointment_cancelled: { icon: <CalendarIcon />, bg: "#fee2e2", fg: "#dc2626" },
  appointment_no_show: { icon: <CalendarIcon />, bg: "#ffedd5", fg: "#ea580c" },
  new_appointment_booked: { icon: <CalendarIcon />, bg: "#dbeafe", fg: "#2563eb" },
  prescription_ready: { icon: <CapsuleIcon />, bg: "#ccfbf1", fg: "#0d9488" },
  new_patient_registered: { icon: <UserPlusIcon />, bg: "#dbeafe", fg: "#2563eb" },
  payment_received: { icon: <CircleDollarSignIcon />, bg: "#fef3c7", fg: "#d97706" },
  sync_completed: { icon: <ShieldCheckIcon />, bg: "#fef3c7", fg: "#d97706" },
  sync_failed: { icon: <ShieldCheckIcon />, bg: "#fee2e2", fg: "#dc2626" },
  ai_scribe_ready: { icon: <SparklesIcon />, bg: "#dbeafe", fg: "#2563eb" },
  inventory_low: { icon: <PackageIcon />, bg: "#ffedd5", fg: "#ea580c" },
  inventory_restock: { icon: <PackageIcon />, bg: "#dcfce7", fg: "#16a34a" },
};

const EVENT_DEFAULT = { icon: <CalendarIcon />, bg: "#f1f5f9", fg: "#64748b" };

/* ── Build the right in-app route for a notification ───────────────────── */

function resolveRoute(n: Notification): string {
  const p = n.payload;
  const patientId = p.patient_id as string | undefined;

  switch (n.event_type) {
    // Patient events → go to that patient's profile
    case "prescription_ready":
    case "new_patient_registered":
      return patientId ? `/patients/${patientId}` : "/patients";

    // Appointment events → go to appointments page
    case "appointment_confirmed":
    case "appointment_cancelled":
    case "appointment_no_show":
    case "new_appointment_booked":
      return "/appointments";

    // System events → settings
    case "subscription_activated":
    case "sync_completed":
    case "sync_failed":
      return "/settings";

    default:
      return "/dashboard";
  }
}

/* ── Helpers ──────────────────────────────────────────────────────────── */

interface NotificationPanelProps {
  /** Unread support-team replies; renders a link row that opens Settings → Support. */
  support?: SupportUnreadSummary;
  notifications: Notification[];
  unreadCount?: number;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onMarkRead: (notificationId: string) => Promise<void>;
  onMarkAllRead: () => Promise<void>;
  onDismiss: (notificationId: string) => Promise<void>;
  onDismissAll?: () => Promise<void>;
  onClose: () => void;
  onLoadMore: () => void;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function getDateGroup(iso: string): "TODAY" | "YESTERDAY" | "EARLIER" {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d >= today) return "TODAY";
  if (d >= yesterday) return "YESTERDAY";
  return "EARLIER";
}

/* ── Component ────────────────────────────────────────────────────────── */

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  unreadCount,
  loading,
  hasMore,
  loadingMore,
  onMarkRead,
  onMarkAllRead,
  onDismiss,
  onDismissAll,
  onClose,
  onLoadMore,
  support,
}) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<CategoryKey>("all");
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Infinite scroll sentinel
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoadMore(); },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  const handleClickItem = useCallback(async (n: Notification) => {
    if (!n.is_read) await onMarkRead(n.notification_id);
    onClose();
    navigate(n.link || resolveRoute(n));
  }, [onMarkRead, onClose, navigate]);

  // Filter notifications by category
  const filtered = activeFilter === "all"
    ? notifications
    : notifications.filter(n => getCategory(n.event_type) === activeFilter);



  // Group by date
  const groups: { label: string; items: Notification[] }[] = [];
  let currentGroup: string | null = null;
  filtered.forEach(n => {
    const group = getDateGroup(n.created_at);
    if (group !== currentGroup) {
      groups.push({ label: group, items: [] });
      currentGroup = group;
    }
    groups[groups.length - 1].items.push(n);
  });

  const effectiveUnreadCount = unreadCount ?? notifications.filter(n => !n.is_read).length;
  const isMobile = typeof window !== "undefined" && window.innerWidth < 480;

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      style={{
        position: isMobile ? "fixed" : "absolute",
        top: isMobile ? 0 : "100%",
        right: isMobile ? 0 : 0,
        bottom: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        marginTop: isMobile ? 0 : 8,
        width: isMobile ? "100vw" : 460,
        maxWidth: isMobile ? "100vw" : "calc(100vw - 32px)",
        height: isMobile ? "100vh" : "auto",
        maxHeight: isMobile ? "100vh" : "calc(100vh - 80px)",
        background: "white",
        borderRadius: isMobile ? 0 : 16,
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
        border: isMobile ? "none" : "1px solid #e5e7eb",
        zIndex: 50,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid #f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>
            Notifications
          </h3>
          {effectiveUnreadCount > 0 && (
            <span
              style={{
                background: "#2563eb",
                color: "white",
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 10,
                lineHeight: "16px",
              }}
            >
              {effectiveUnreadCount} new
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {effectiveUnreadCount > 0 && (
            <button
              type="button"
              onClick={() => { void onMarkAllRead(); }}
              style={{
                background: "transparent",
                border: "none",
                color: "#6b7280",
                fontSize: 13,
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <CheckIcon /> Mark all read
            </button>
          )}
          {notifications.length > 0 && onDismissAll && (
            <button
              type="button"
              onClick={() => { void onDismissAll(); }}
              style={{
                background: "transparent",
                border: "none",
                color: "#6b7280",
                fontSize: 13,
                cursor: "pointer",
                padding: 0,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <TrashIcon /> Clear all
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              aria-label="Close notifications"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "#6b7280",
                fontSize: 20,
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {support && support.total > 0 && (
        <button
          type="button"
          onClick={() => {
            onClose();
            const first = support.tickets[0];
            navigate(`/settings?section=support${first ? `&ticket=${first.ticket_id}` : ""}`);
          }}
          style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
            padding: "12px 20px", background: "#e0f2fe", border: "none",
            borderBottom: "1px solid #bae6fd", cursor: "pointer", flexShrink: 0,
          }}
        >
          <span style={{
            width: 32, height: 32, borderRadius: 16, background: "#0284c7", color: "white",
            display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#0c4a6e" }}>
              {support.total} new message{support.total > 1 ? "s" : ""} from the support team
            </span>
            <span style={{ display: "block", fontSize: 12, color: "#0369a1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {support.tickets[0]?.subject} · Open support chat ›
            </span>
          </span>
        </button>
      )}

      {/* ── Filter Tabs ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          padding: "0 20px 12px",
          flexWrap: "wrap",
        }}
      >
        {CATEGORY_LABELS.map(({ key, label }) => {
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveFilter(key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "6px 12px",
                borderRadius: 20,
                border: isActive ? "none" : "1px solid #e5e7eb",
                background: isActive ? "#2563eb" : "white",
                color: isActive ? "white" : "#374151",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Separator ───────────────────────────────────────────────── */}
      <div style={{ borderBottom: "1px solid #e5e7eb" }} />

      {/* ── Notification List ───────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {loading && notifications.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 24,
                background: "#f3f4f6",
                color: "#9ca3af",
                margin: "0 auto 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-hidden="true"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#374151", marginBottom: 4 }}>
              You're all caught up
            </div>
            <div style={{ fontSize: 12, color: "#9ca3af" }}>
              New activity will show up here.
            </div>
          </div>
        ) : (
          <>
            {groups.map(group => (
              <div key={group.label}>
                {/* Date group header */}
                <div
                  style={{
                    padding: "8px 20px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  {group.label}
                </div>
                {group.items.map(n => {
                  const visual = EVENT_VISUAL[n.event_type] || EVENT_DEFAULT;
                  const categoryLabel = EVENT_CATEGORY_LABEL[n.event_type] || "Notification";
                  const isAi = n.event_type === "ai_scribe_ready";
                  return (
                    <div
                      key={n.notification_id}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: "14px 20px",
                        borderBottom: "1px solid #f3f4f6",
                        background: n.is_read ? "white" : "#f9fafb",
                        cursor: "pointer",
                        transition: "background 0.15s",
                      }}
                      onClick={() => { void handleClickItem(n); }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "#f3f4f6"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = n.is_read ? "white" : "#f9fafb"; }}
                    >
                      {/* Icon */}
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          background: visual.bg,
                          color: visual.fg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {visual.icon}
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginBottom: 2,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: n.is_read ? 500 : 700,
                              color: "#111827",
                            }}
                          >
                            {n.title}
                          </span>
                          {!n.is_read && (
                            <span
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: "#2563eb",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {isAi && (
                            <span
                              style={{
                                background: "#eff6ff",
                                color: "#2563eb",
                                fontSize: 10,
                                fontWeight: 600,
                                padding: "1px 6px",
                                borderRadius: 4,
                                border: "1px solid #bfdbfe",
                              }}
                            >
                              AI
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: "#4b5563",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            lineHeight: 1.4,
                            marginBottom: 4,
                          }}
                        >
                          {n.body}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#9ca3af" }}>
                          <span>{relativeTime(n.created_at)}</span>
                          <span style={{ color: "#d1d5db" }}>•</span>
                          <span>{categoryLabel}</span>
                        </div>
                      </div>
                      {/* Dismiss X */}
                      <button
                        type="button"
                        aria-label="Dismiss notification"
                        onClick={(e) => {
                          e.stopPropagation();
                          void onDismiss(n.notification_id);
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#9ca3af",
                          padding: 4,
                          alignSelf: "flex-start",
                          fontSize: 18,
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && (
              <div style={{ padding: "10px 0", textAlign: "center", color: "#9ca3af", fontSize: 12 }}>
                Loading more…
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};
